import type { SyntheticEvent } from "react";
import { create } from "zustand";

/** What the owner was trying to do when the trial stopped them — picks the pop-up's title. */
export type PaidPlanReason = "seats" | "whatsapp" | "a2p" | "autotopup" | "numbers";

export interface PaidPlanRequest {
    reason?: PaidPlanReason;
    /** CT-579: carry on with what was clicked once the paid plan has started. */
    onStarted?: () => void;
}

/**
 * CT-566 / CT-579: the trial pop-up is opened from many places, so it lives in one
 * store and is drawn once by TrialCapDialogHost in the dashboard layout.
 */
type TrialCapState = {
    open: boolean;
    reason: PaidPlanReason;
    onStarted: (() => void) | null;
    /** True while the dashboard host is on screen. Outside it (invite accept, login) nobody can draw the pop-up. */
    hostMounted: boolean;
    setOpen(open: boolean): void;
    setHostMounted(mounted: boolean): void;
};

export const useTrialCapStore = create<TrialCapState>((set) => ({
    open: false,
    reason: "seats",
    onStarted: null,
    hostMounted: false,
    setOpen: (open) => set(open ? { open } : { open, onStarted: null }),
    setHostMounted: (hostMounted) => set({ hostMounted }),
}));

/** Callers may pass this straight to onClick, so anything that isn't a request reads as none. */
function asRequest(value: unknown): PaidPlanRequest {
    return value && typeof value === "object" && ("reason" in value || "onStarted" in value)
        ? (value as PaidPlanRequest)
        : {};
}

export function openTrialCapDialog(request?: PaidPlanRequest | SyntheticEvent): void {
    const { reason = "seats", onStarted } = asRequest(request);
    useTrialCapStore.setState({ open: true, reason, onStarted: onStarted ?? null });
}

/** Opens the pop-up only where it can be drawn; false means the caller must show the refusal itself. */
export function tryOpenTrialCapDialog(request?: PaidPlanRequest): boolean {
    if (!useTrialCapStore.getState().hostMounted) return false;
    openTrialCapDialog(request);
    return true;
}
